import React, { useState, useRef } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  CircularProgress,
  Alert,
  TextField,
  Button,
  Typography,
  Paper,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import API from "../../../axios/instances/API";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPCHA_SITE_KEY, RECAPCHA_ENABLED } from "src/storage/constants";
import styled from "styled-components";

const initialValues = {
  email: "",
  submit: false,
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Must be a valid email")
    .max(255)
    .required("Email is required"),
});

const Card = styled(Paper)`
  padding: 30px 40px 40px 40px;
  width: calc(min(520px, 96vw));
  min-height: 140px;

  @media (max-width: 560px) {
    padding: 20px;

    h2 {
      font-size: 22px;
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: fit-content;
`;

const RecoverPassword = () => {
  const [alertText, setAlertText] = useState(false);
  const [successText, setSuccessText] = useState(false);
  const recaptchaRef = useRef(null);

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setAlertText(false);
    setSuccessText(false);

    let recaptchaToken = null;
    try {
      if (RECAPCHA_ENABLED && !recaptchaRef.current) {
        throw new Error("reCAPTCHA reference is null or undefined");
      }
      if (RECAPCHA_ENABLED) {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    } catch (error) {
      setAlertText("Failed recapcha verification");
      return setSubmitting(false);
    }

    try {
      await API.post("dashboard/auth/forgot-password/", {
        email: values.email.trim(),
        "g-recaptcha-response": RECAPCHA_ENABLED ? recaptchaToken : "bypass",
      });
      setSuccessText("Recovery link sent. Check your email");
      setSubmitting(false);
      resetForm();
    } catch (error) {
      setSubmitting(false);
      setAlertText("That email is not in our records");
      resetForm();
    }
  };

  return (
    <>
      <Helmet title="Spiffy | Recover Password" />
      <Wrapper>
        <Card>
          <Typography variant="h3" component="h3" align="center">
            Recover Password
          </Typography>
          <Typography
            component="h5"
            variant="subtitle2"
            align="center"
            color="textSecondary"
          >
            Enter your email to receive recovery link
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                {errors.submit && (
                  <Alert sx={{ mt: 2, mb: 2 }} severity="error">
                    {errors.submit}
                  </Alert>
                )}
                <TextField
                  type="email"
                  name="email"
                  size="small"
                  label="Email Address"
                  value={values.email}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  sx={{ mt: 4 }}
                />
                {RECAPCHA_ENABLED && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPCHA_SITE_KEY}
                    size="invisible"
                    badge="bottomright"
                  />
                )}

                <Button
                  type="submit"
                  fullWidth
                  sx={{ mt: 4 }}
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={20} />}
                >
                  {isSubmitting ? "Sending" : "Recover Password"}
                </Button>

                {successText && (
                  <Alert
                    sx={{ mt: 2 }}
                    type="success"
                    onClose={() => setSuccessText(false)}
                  >
                    {successText}
                  </Alert>
                )}
                {alertText && (
                  <Alert
                    severity="error"
                    onClose={() => setAlertText(false)}
                    sx={{ mt: 2 }}
                  >
                    {alertText}
                  </Alert>
                )}
              </form>
            )}
          </Formik>
        </Card>
      </Wrapper>
    </>
  );
};

export default RecoverPassword;
